import React, { ReactNode, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTurnoActual } from "src/pages/home/room-detail/hooks/turnos"
import { RootState } from "src/store/store"
import { useProfile } from "../hooks/useProfile"
import { useQuery } from "@apollo/client"
import { useListenHabitacionesSubscription } from "src/gql/schema"
import { GET_ROOMS } from "src/pages/home/graphql/queries/rooms.query"
import { setTurn } from "src/store/turn/turnSlice"
import { setIsLoading, setRooms, setRoomsDimensions } from "src/store/rooms/roomsSlice"
import { startRefetchSelectedRoom } from "src/pages/home/store/thunks/rooms.thunk"
import { ROOMS_GRID_SKELETON, ROOMS_LENGTH_SKELETON, ROOMS_POSITIONS_SKELETON } from "../constants/localStorage"
import { requestWsReconnect } from "src/graphql"

const RoomsProvider = ({ children }: { children: ReactNode }) => {
    const dispatch = useDispatch()

    const { rooms, selectedRoom } = useSelector((root: RootState) => root.rooms)
    const turno = useTurnoActual()
    const { hotel_id, usuario_id } = useProfile()

    const { data: hotelesQueryData, refetch: refetchRooms, loading } = useQuery(GET_ROOMS, {
        variables: {
            hotel_id,
            usuario_id,
        },
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true,
    })

    const { data } = useListenHabitacionesSubscription({
        variables: {
            hotelIdFilter: hotel_id,
            usuario_id
        },
        fetchPolicy: "no-cache",
    })

    useEffect(() => {
        localStorage.setItem("turno", JSON.stringify(turno || "{}"))
        if (turno) {
            dispatch(setTurn({ nombre: turno?.nombre, turno_id: turno?.turno_id }))
        }
    }, [turno])

    useEffect(() => {
        if (!hotelesQueryData?.habitaciones) {
            return
        }
        const habitaciones = hotelesQueryData.habitaciones
        const roomsDimensions = {
            y: Math.max(...habitaciones.map((h: any) => h.posicion.x)),
            x: Math.max(...habitaciones.map((h: any) => h.posicion.y))
        }

        dispatch(setRooms(habitaciones as any))
        dispatch(setRoomsDimensions(roomsDimensions))

        // Se guarda el layout (cuántas cards, dónde va cada una y el tamaño de la
        // cuadrícula) para que el skeleton de la siguiente carga dibuje las cards en la
        // misma posición que las reales, en lugar de una fila de cards genéricas.
        localStorage.setItem(ROOMS_LENGTH_SKELETON, String(habitaciones.length))
        localStorage.setItem(
            ROOMS_POSITIONS_SKELETON,
            JSON.stringify(habitaciones.map((h: any) => ({ x: h.posicion.x, y: h.posicion.y })))
        )
        localStorage.setItem(ROOMS_GRID_SKELETON, JSON.stringify(roomsDimensions))
    }, [hotelesQueryData])

    useEffect(() => {
        // Si cambió la habitación abierta en el drawer, se vuelve a consultar completa (la suscripción no trae todos
        // los campos del detalle) sin cerrar el drawer; mientras carga se muestra un skeleton
        if (data?.checkHabitaciones?.some((h) => h.habitacion_id === selectedRoom?.habitacion_id)) {
            dispatch(startRefetchSelectedRoom(selectedRoom.habitacion_id))
        }

        if (data?.checkHabitaciones) {
            const updatedRooms = rooms.map((r) => {
                const roomChanged = data?.checkHabitaciones?.find((chR) => r.habitacion_id === chR.habitacion_id)
                return roomChanged ? roomChanged : r
            })
            dispatch(setRooms(updatedRooms as any))
        }
    }, [data])

    useEffect(() => {
        dispatch(setIsLoading(loading))
    }, [loading])

    useEffect(() => {
        const resync = async (force: boolean) => {
            if (document.visibilityState !== "visible") return
            // La decisión es sincrónica y va antes de cualquier `await`, para que los
            // providers compartan la misma ronda de resync.
            const reconnection = requestWsReconnect({ force })
            // Solo se resincroniza si ya pasó el intervalo mínimo o si la conexión está
            // caída; si no, no se hace nada: ni socket nuevo, ni skeleton, ni refetch
            // (ver requestWsReconnect).
            if (!reconnection) return

            dispatch(setIsLoading(true))
            // Se espera la confirmación de la conexión ANTES de refetchear: así, si algo
            // cambia mientras el refetch está en curso, la suscripción (ya viva) lo
            // recibe y esa actualización llega después, sin que el refetch la pise con un
            // snapshot más viejo.
            await reconnection
            refetchRooms()
        }
        const onVisibilityChange = () => resync(false)
        const onOnline = () => resync(true)
        document.addEventListener("visibilitychange", onVisibilityChange)
        window.addEventListener("online", onOnline)
        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange)
            window.removeEventListener("online", onOnline)
        }
    }, [refetchRooms])

    return <>{children}</>
}

export default RoomsProvider
